// Original soldier sprites, drawn in code: a top-down trooper in a helmet
// with a rifle across the body, facing +x (rotation 0 in the scene). One
// canvas texture per uniform colour, five frames each: idle and a four-step
// walk (boots swing, body bobs). Commando-era arcade look as inspiration
// only; nothing here is copied from any game. Replace with hand-drawn
// frames of the same names when an artist takes over.
import type Phaser from 'phaser';

export const SOLDIER_FRAME = 40; // px, square; the body is ~28 px (PLAYER_RADIUS 14)
export const SOLDIER_WALK_FRAMES = 4;

interface Palette {
  uniform: string;
  uniformDark: string;
  helmet: string;
  helmetLight: string;
}

const SKIN = '#deb88c';
const BOOT = '#3a2c1e';
const RIFLE = '#34363a';
const STOCK = '#78542e';

// Team colours match the scoreboard and map preview (blue, red, green, gold).
export const SOLDIER_KEYS = {
  me: 'soldier-me',
  enemy: 'soldier',
  team: (team: number): string => `soldier-t${team}`,
};

const PALETTES: Record<string, Palette> = {
  'soldier-me': { uniform: '#5c7a3a', uniformDark: '#3e5427', helmet: '#4b6630', helmetLight: '#7b9a52' },
  soldier: { uniform: '#a84c3a', uniformDark: '#73301f', helmet: '#8c3e2e', helmetLight: '#c9775f' },
  'soldier-t1': { uniform: '#4870b8', uniformDark: '#2f4b80', helmet: '#3d5fa0', helmetLight: '#7f9fd8' },
  'soldier-t2': { uniform: '#b0463c', uniformDark: '#772c24', helmet: '#953b32', helmetLight: '#d47a6f' },
  'soldier-t3': { uniform: '#549654', uniformDark: '#366236', helmet: '#467f46', helmetLight: '#86c086' },
  'soldier-t4': { uniform: '#c0a048', uniformDark: '#82692c', helmet: '#a3873a', helmetLight: '#dcc47c' },
};

function ellipse(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, fill: string): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

function limb(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, width: number, colour: string): void {
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.strokeStyle = colour;
  ctx.stroke();
}

// One frame at (ox, oy). `step` is the walk phase in -1..1 (0 = feet together),
// `bob` a small body shift along the facing.
function drawSoldier(ctx: CanvasRenderingContext2D, ox: number, oy: number, p: Palette, step: number, bob: number): void {
  const cx = ox + SOLDIER_FRAME / 2 + bob;
  const cy = oy + SOLDIER_FRAME / 2;
  // Ground shadow, offset a little as if lit from the top-left.
  ellipse(ctx, cx + 1 - bob, cy + 2, 13, 11, 'rgba(0,0,0,0.28)');
  // Boots swing front/back on either side of the body.
  ellipse(ctx, cx + step * 5 - 2, cy - 10, 4, 3, BOOT);
  ellipse(ctx, cx - step * 5 - 2, cy + 10, 4, 3, BOOT);
  // Backpack behind the torso.
  ctx.fillStyle = p.uniformDark;
  ctx.fillRect(cx - 13, cy - 6, 6, 12);
  // Torso: shoulders wide across the facing, with a darker belt line.
  ellipse(ctx, cx - 2, cy, 9, 12, p.uniform);
  ctx.fillStyle = p.uniformDark;
  ctx.fillRect(cx - 4, cy - 12, 2, 24);
  // Arms: right hand on the grip, left hand forward on the foregrip.
  limb(ctx, cx - 2, cy + 9, cx + 6, cy + 7, 5, p.uniform);
  limb(ctx, cx - 2, cy - 8, cx + 11, cy + 3, 5, p.uniform);
  // Rifle along the facing: stock, body, barrel.
  ctx.fillStyle = STOCK;
  ctx.fillRect(cx - 5, cy + 3, 9, 4);
  ctx.fillStyle = RIFLE;
  ctx.fillRect(cx + 2, cy + 3, 19, 4);
  ctx.fillRect(cx + 8, cy + 6, 5, 3); // magazine
  ctx.fillStyle = '#1e2022';
  ctx.fillRect(cx + 17, cy + 4, 5, 2); // muzzle
  // Hands.
  ellipse(ctx, cx + 6, cy + 7, 2.6, 2.6, SKIN);
  ellipse(ctx, cx + 11, cy + 3, 2.6, 2.6, SKIN);
  // Helmet: dome with a rim and a highlight; a sliver of face toward the aim.
  ellipse(ctx, cx - 1, cy, 4, 7, SKIN);
  ellipse(ctx, cx - 3, cy, 9, 9, p.helmet);
  ctx.beginPath();
  ctx.ellipse(cx - 3, cy, 9, 9, 0, 0, Math.PI * 2);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = p.uniformDark;
  ctx.stroke();
  ellipse(ctx, cx - 6, cy - 3, 3.2, 2.4, p.helmetLight);
}

// Builds every soldier sheet once per scene. Frame names: 'idle',
// 'walk0'..'walk3'. Idempotent.
export function makeSoldierSheets(scene: Phaser.Scene): void {
  for (const key of Object.keys(PALETTES)) {
    if (scene.textures.exists(key)) continue;
    const frames = 1 + SOLDIER_WALK_FRAMES;
    const tex = scene.textures.createCanvas(key, SOLDIER_FRAME * frames, SOLDIER_FRAME);
    if (!tex) continue;
    const ctx = tex.getContext();
    const p = PALETTES[key]!;
    drawSoldier(ctx, 0, 0, p, 0, 0);
    for (let i = 0; i < SOLDIER_WALK_FRAMES; i++) {
      const phase = (i / SOLDIER_WALK_FRAMES) * Math.PI * 2;
      drawSoldier(ctx, SOLDIER_FRAME * (i + 1), 0, p, Math.sin(phase), Math.abs(Math.cos(phase)) * -1 + 0.5);
    }
    tex.refresh();
    tex.add('idle', 0, 0, 0, SOLDIER_FRAME, SOLDIER_FRAME);
    for (let i = 0; i < SOLDIER_WALK_FRAMES; i++) tex.add(`walk${i}`, 0, SOLDIER_FRAME * (i + 1), 0, SOLDIER_FRAME, SOLDIER_FRAME);
  }
}
